// src/components/credits/CompanyBillingBody.jsx — the company's invoicing record
// as a BODY, mounted by two surfaces that resolve the company differently.
//
// ⭐ TWO SURFACES, TWO RESOLUTIONS, ONE BODY:
//   • `/billing-details` (sections/BillingDetails.jsx, avatar menu) resolves the
//     company from the ACTIVE WORKSPACE, because `customers[]` is an ARRAY and a
//     standalone page has no other way to disambiguate a two-company admin.
//   • the "Dati azienda" tab (tenantAdmin/pages/CompanyDetailsPage.jsx) takes it
//     from the console layout's company selector.
//
// ⛔ Which is exactly why this component does NOT resolve the customer itself and
// takes no part in choosing one: it reads no `activeTenant`, calls no
// `/credits/balance`, and holds no state. Give it an id or it renders the refusal.
// ⛔ And it is NOT a route: no `Navigate`, no superadmin/adminMode bounce — each
// caller owns its own routing (only `/billing-details` has one to do).
//
// ⚠️ The gate here is a PRE-GATE only, kept because ⛔ no call to
// `/admin/customers/{cid}*` may be made without `isCustomerAdminOf(cid)` first.
// `CustomerBillingForm` still hides itself on a 403 CUSTOMER_ADMIN_REQUIRED (which
// is also what an unknown id returns — the gate precedes the lookup), and the
// server remains the real authority.
//
// ⛔ `canEditCountry` is a SUPERADMIN right and nothing else: the backend answers
// 422 CUSTOMER_INVALID to a non-superadmin who sends `country_code` — refused, not
// stripped — so no customer-admin surface may offer the input. Neither caller can
// pass it in; it is read from the claims here.
//
// ⭐ `variant` is CHROME only, and since 2026-09-27 both variants share the section
// card of the Gestione Workspace look (components/ui/SectionCard.jsx): the round
// icon tile + title + intro in the card header, the form `embedded` inside, the
// centred 1280px column. 'page' (default, /billing-details) titles it with an <h1>
// — the page has no other title; 'tab' (the "Dati azienda" tab) with an <h2>,
// because the console layout owns the <h1>. The gate and the form are identical.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext.jsx';
import CustomerBillingForm from './CustomerBillingForm.jsx';
import SectionCard, { PAGE_COLUMN, CARD_BODY } from '../ui/SectionCard.jsx';
import { COLORS } from '../../constants/theme.js';

export default function CompanyBillingBody({ customerId, variant = 'page' }) {
  const { t } = useTranslation('admin');
  const { isSuperadmin, isCustomerAdminOf } = useAuth();

  // ⛔ THE gate. No id, or an id the user does not administer → the explanation,
  // never the form and never a request.
  const allowed = customerId && isCustomerAdminOf?.(customerId);

  return (
    <div style={PAGE_COLUMN}>
      <SectionCard
        icon={Receipt}
        title={t('billing.pageTitle')}
        subtitle={t('billing.intro')}
        headingLevel={variant === 'tab' ? 2 : 1}
      >
        <div style={CARD_BODY}>
          {allowed ? (
            <CustomerBillingForm customerId={customerId} canEditCountry={isSuperadmin} embedded />
          ) : (
            <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: 0 }}>{t('billing.notAllowed')}</p>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
